import { supabase } from "./supabase";

/** Company-level write API (alerts, zones, fleet). Throws Error(message). */
export async function orgApi<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const r = await fetch(`/api/org/${path}`, {
    method,
    headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}`, ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const res = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(res?.error ?? `Request failed (${r.status})`);
  return res as T;
}

/** Companies where the signed-in user may manage alerts/fleet (owner), or "all" for Skyward staff. */
export async function managedOrgs(userId: string): Promise<Set<string> | "all"> {
  const { data } = await supabase.from("memberships").select("org_id, role").eq("user_id", userId);
  if ((data ?? []).some((m) => m.role === "skyward_admin" || m.role === "skyward_tech")) return "all";
  return new Set((data ?? []).filter((m) => m.role === "org_owner").map((m) => m.org_id));
}
export const canManage = (m: Set<string> | "all" | null, org: string) => m === "all" || !!m?.has(org);

// ---------------------------------------------------------------- alerts
export type AlertType = "overspeed" | "zone_enter" | "zone_exit" | "zone_any" | "zone_outside_time" | "loiter" | "offline"
  | "ignition_on" | "ignition_off" | "sos" | "power_cut" | "low_battery";
/** Order shown in the "Alert me when…" picker. */
export const ALERT_ORDER: AlertType[] = ["zone_any", "zone_enter", "zone_exit", "zone_outside_time", "overspeed", "loiter", "offline",
  "ignition_on", "ignition_off", "sos", "power_cut", "low_battery"];
export const ALERT_LABEL: Record<AlertType, string> = {
  overspeed: "Speeding", zone_enter: "Enters a zone", zone_exit: "Leaves a zone", zone_any: "Enters or leaves a zone",
  zone_outside_time: "Outside a zone during set hours", loiter: "Not moving (loitering)", offline: "Stops reporting (offline)",
  ignition_on: "Ignition on", ignition_off: "Ignition off", sos: "SOS button", power_cut: "Power cut / tracker unplugged", low_battery: "Low battery",
};
export const ALERT_HELP: Partial<Record<AlertType, string>> = {
  zone_any: "One event each time the object enters or leaves — all zones, or just the one you pick.",
  zone_outside_time: "E.g. outside the depot after hours. Times are Fiji time; a window like 18:00–06:00 runs overnight.",
  loiter: "Counts as not moving when under 3 km/h and within about 60 m. Checked every minute.",
  overspeed: "Everywhere, or only while inside one zone (e.g. 20 km/h on a site).",
  ignition_on: "Needs the tracker's ignition (ACC) wire connected.", ignition_off: "Needs the tracker's ignition (ACC) wire connected.",
  sos: "Only for trackers with an SOS button.", power_cut: "Sent by the tracker when main power is lost (most hard-wired units).",
};
export type Channel = "email" | "inapp" | "push" | "log";
export const CHANNEL_LABEL: Record<Channel, string> = { email: "Email", inapp: "In-app", push: "App", log: "Log only" };
export type AlertRule = { id: string; org_id: string; device_id: string | null; type: AlertType; params: Record<string, any>;
  recipients: string[]; enabled: boolean; name: string | null; created_at: string; channels?: Channel[] | null; is_default?: boolean };
export type AlertEvent = { id: number; org_id: string; device_id: string; rule_id: string | null; type: AlertType; at: string;
  lat: number | null; lng: number | null; message: string; notified_at: string | null; notify_error: string | null; ack_at: string | null; inapp?: boolean };
export type Zone = { id: string; org_id: string; name: string; kind: "circle" | "polygon"; center_lat: number; center_lng: number;
  radius_m: number | null; polygon: [number, number][] | null; color: string; is_global?: boolean };

export const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const mins = (m: number) => (m >= 60 ? `${+(m / 60).toFixed(1)} h` : `${m} min`);
export function daysText(days?: number[]) {
  if (!days?.length || days.length === 7) return "every day";
  const k = [...days].sort().join();
  if (k === "1,2,3,4,5") return "Mon–Fri";
  if (k === "0,6") return "weekends";
  return [1, 2, 3, 4, 5, 6, 0].filter((d) => days.includes(d)).map((d) => DAY_SHORT[d]).join(", ");
}

export function ruleSummary(r: AlertRule, zones: Zone[]) {
  const base = ruleSummaryBase(r, zones);
  return r.params?.skip_demo && !r.device_id ? `${base} · ignores demo devices` : base;   // V3.9.3
}
function ruleSummaryBase(r: AlertRule, zones: Zone[]) {
  const p = r.params ?? {};
  const zn = (id?: string) => zones.find((z) => z.id === id)?.name ?? "zone";
  if (r.type === "overspeed") return `Over ${p.limit_kmh} km/h${p.geofence_id ? ` inside ${zn(p.geofence_id)}` : ""}`;
  if (r.type === "offline") return `No report for ${mins(p.minutes)}`;
  if (r.type === "zone_enter" || r.type === "zone_exit") return `${r.type === "zone_enter" ? "Enters" : "Leaves"} ${zn(p.geofence_id)}`;
  if (r.type === "zone_any") return p.geofence_id ? `Enters or leaves ${zn(p.geofence_id)}` : "Enters or leaves any zone";
  if (r.type === "zone_outside_time") return `Outside ${zn(p.geofence_id)} ${p.start}–${p.end}, ${daysText(p.days)}${p.repeat_min ? ` · repeats every ${mins(p.repeat_min)}` : " · once per window"}`;
  if (r.type === "loiter") return `Not moving for ${mins(p.minutes)}${p.where === "inside" ? ` inside ${zn(p.geofence_id)}` : p.where === "outside" ? ` outside ${zn(p.geofence_id)}` : ""}`;
  return ALERT_LABEL[r.type];
}
export function channelsText(r: AlertRule) {
  const ch = r.channels ?? (r.recipients.length ? ["email", "inapp"] : ["inapp"]);
  const parts = ch.filter((c) => c !== "push").map((c) => (c === "email" ? `Email (${r.recipients.length})` : "In-app"));
  return parts.join(" + ") || "—";
}

// ---------------------------------------------------------------- people (company users + manual contacts)
export type Person = { id: string; email: string | null; name: string | null; phone: string | null; role: string | null };
export type Contact = { id: string; org_id: string; name: string; email: string | null; phone: string | null; role: string | null; notes: string | null };
export type People = { users: Person[]; contacts: Contact[] };
const peopleCache = new Map<string, Promise<People>>();
/** Managers only (owner / Skyward staff). Rejects for others — callers fall back to manual entry. */
export function loadPeople(org: string, fresh = false): Promise<People> {
  if (fresh || !peopleCache.has(org)) peopleCache.set(org, orgApi<People>("GET", `people/${org}`).catch((e) => { peopleCache.delete(org); throw e; }));
  return peopleCache.get(org)!;
}
